import { pollForPaidEntitlement } from './entitlementPoller.js';

const HANDBOOK_PRICE_CENTS = 1200;
const HANDBOOK_CURRENCY = 'usd';
const panel = { childId: null, productId: null, errorMessage: '', mounted: null, phase: 'idle', processingMessage: '', syncToken: 0 };

let paymentContext = null;
let paymentServicePromise = null;

function getPaymentService() {
  if (!paymentServicePromise) paymentServicePromise = import('./paymentService.ts');
  return paymentServicePromise;
}

function setPhase(phase, errorMessage = '', processingMessage = '') {
  panel.phase = phase;
  panel.errorMessage = errorMessage;
  panel.processingMessage = processingMessage;
  renderPanel();
}

function teardown(resetIdentity = false) {
  panel.mounted?.paymentElement?.destroy();
  panel.mounted = null;
  panel.errorMessage = '';
  panel.phase = 'idle';
  panel.processingMessage = '';
  if (resetIdentity) {
    panel.childId = null;
    panel.productId = null;
  }
  paymentContext?.elements.paymentElement?.replaceChildren();
  renderPanel();
}

function renderPanel() {
  const elements = paymentContext?.elements;
  if (!elements?.screenPay) return;
  const isMounted = Boolean(panel.mounted);
  elements.paymentElement.style.display = isMounted ? 'block' : 'none';
  elements.btnPaySubmit.style.display = isMounted ? 'inline-flex' : 'none';
  elements.btnPaySubmit.disabled = panel.phase === 'submitting';
  elements.btnPaySubmit.textContent = panel.phase === 'submitting' ? 'Confirming...' : 'Pay now';
  elements.paymentProcessing.style.display = ['loading_intent', 'submitting', 'pending'].includes(panel.phase) ? 'block' : 'none';
  elements.paymentProcessing.textContent = panel.processingMessage || (panel.phase === 'loading_intent' ? 'Preparing secure checkout...' : 'Confirming payment...');
  elements.paymentError.style.display = panel.errorMessage ? 'block' : 'none';
  elements.paymentError.textContent = panel.errorMessage;
}

async function createAndMount(childId, productId) {
  const token = ++panel.syncToken;
  setPhase('loading_intent');
  try {
    const { fetchPaymentIntent, mountStripeElements } = await getPaymentService();
    const { clientSecret } = await fetchPaymentIntent({ amount: HANDBOOK_PRICE_CENTS, childId, currency: HANDBOOK_CURRENCY, productId });
    const mounted = await mountStripeElements(clientSecret);
    if (token !== panel.syncToken) {
      mounted.paymentElement.destroy();
      return;
    }
    panel.childId = childId;
    panel.productId = productId;
    panel.mounted = mounted;
    setPhase('mounted');
  } catch (error) {
    setPhase('failed', error instanceof Error ? error.message : 'Could not start payment. Please try again.');
  }
}

async function waitForPaidEntitlement() {
  try {
    return await pollForPaidEntitlement(panel.childId, panel.productId);
  } catch (error) {
    if (!(error instanceof Error) || error.message !== 'timeout') throw error;
    setPhase('pending', '', 'Payment processing — we\'ll unlock your questionnaire the moment it clears.');
    return pollForPaidEntitlement(panel.childId, panel.productId, { timeoutMs: 10 * 60 * 1000 });
  }
}

async function handleSubmit() {
  if (!paymentContext?.state.user || panel.phase !== 'mounted' || !panel.mounted) return;
  setPhase('submitting');
  const { handlePaymentResult } = await getPaymentService();
  const result = await panel.mounted.stripe.confirmPayment({ elements: panel.mounted.elements, redirect: 'if_required' });
  const payment = handlePaymentResult(result);
  if (!payment.ok) {
    setPhase('failed', payment.message);
    return;
  }
  try {
    paymentContext.state.entitlement = await waitForPaidEntitlement();
    paymentContext.state.screenIntent = 'wizard';
    teardown(false);
    paymentContext.routeScreen();
  } catch (error) {
    setPhase('failed', error instanceof Error ? error.message : 'Payment succeeded, but verification is still pending.');
  }
}

export function initPaymentForm(context) {
  paymentContext = context;
  context.elements.btnPaySubmit?.addEventListener('click', () => void handleSubmit());
  renderPanel();
}

export function syncPaymentForm({ childId, productId, shouldShow }) {
  if (!paymentContext?.elements.screenPay) return;
  if (!shouldShow || !paymentContext.state.user || !childId || !productId) {
    teardown(true);
    return;
  }
  if (panel.childId !== childId || panel.productId !== productId) teardown(false);
  if (panel.phase === 'idle') {
    void createAndMount(childId, productId);
    return;
  }
  renderPanel();
}
